import { formatUtcDateTime, halifaxParts, halifaxWallToDate } from "./clock";
import { blankAircraft } from "./airports";
import type { AircraftSpec, DraftMission, FlightLeg } from "./types";

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

/**
 * Derive the SimBrief airline code from the company's flightNumberPrefix.
 * Returns the normalized prefix when it is 2–4 letters; otherwise returns an
 * empty string so SimBrief assigns no airline code rather than defaulting to CEL.
 */
export function simbriefAirlineCode(prefix: string | null | undefined): string {
  const p = String(prefix ?? "").trim().toUpperCase().replace(/[^A-Z]/g, "").slice(0, 4);
  return p.length >= 2 ? p : "";
}

/** @deprecated Use {@link simbriefAirlineCode} derived from the company's flightNumberPrefix. */
export const SIMBRIEF_AIRLINE = "";

/**
 * SimBrief's built-in baggage per passenger, in pounds.
 * The OFP cargo line is this × passengers, plus any freight.
 * https://forum.navigraph.com/t/weights-dont-match-and-calcs-are-off/7885
 */
export const SIMBRIEF_INCLUDED_BAG_LB = 55;

/**
 * Stated ticket cargo at or under this per passenger is normal bags.
 * Those pounds are already covered by {@link SIMBRIEF_INCLUDED_BAG_LB}
 * and are not extra freight. 5 × 80 = 400 lb — bags, not a freight load.
 */
export const SIMBRIEF_NORMAL_BAG_LB_PER_PAX = 80;

/** Inbox, trip edit, and Flight plans — cargo means extra freight. */
export const EXTRA_CARGO_HINT =
  "Extra cargo only, beyond bags included with each passenger. About 80 lb per person is already included and is not added on the flight plan.";

/** These RFQs are freight (clubs, medical kit), even when the pounds look like bags. */
const EXTRA_CARGO_FLIGHT_TYPES = new Set(["golf", "medical"]);

/**
 * Pounds of SimBrief freight for a ticket cargo figure.
 * Pax count drives SimBrief passengers (and the built-in bag allowance).
 * Cargo is extra only: zero passengers (ferry), golf, medical, or a stated
 * weight above {@link SIMBRIEF_NORMAL_BAG_LB_PER_PAX} per person.
 * At or under that ceiling the stated pounds are not sent.
 */
export function simbriefExtraFreightLb(
  paxCount: number,
  cargoLb: number,
  flightType?: string,
): number {
  const pax = Number.isFinite(paxCount) ? Math.max(0, Math.round(paxCount)) : 0;
  const cargo = Number.isFinite(cargoLb) ? Math.max(0, Math.round(cargoLb)) : 0;
  if (cargo <= 0) return 0;
  if (pax <= 0) return cargo;
  if (flightType && EXTRA_CARGO_FLIGHT_TYPES.has(flightType)) return cargo;
  if (cargo <= pax * SIMBRIEF_NORMAL_BAG_LB_PER_PAX) return 0;
  return cargo;
}

/**
 * SimBrief `cargo` is freight in thousands of pounds (API example `5.0` = 5000 lb).
 * Raw pounds (`cargo=400`) ask for 400,000 lb and the OFP clamps near max payload.
 * `0` is explicit so an omitted value does not become AUTO freight.
 */
export function simbriefCargoParam(freightLb: number): string {
  const lb = Number.isFinite(freightLb) ? Math.max(0, Math.round(freightLb)) : 0;
  if (lb <= 0) return "0";
  const text = (lb / 1000).toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
  return text.includes(".") ? text : `${text}.0`;
}

/** Public dispatch-options prefill (no API key). */
export const DISPATCH_OPTIONS_URL = "https://dispatch.simbrief.com/options/custom";

/** Legacy dispatch.php prefill — same query params, used if the options host is blocked. */
export const LEGACY_DISPATCH_URL = "https://www.simbrief.com/system/dispatch.php";

declare global {
  interface Window {
    simbriefsubmit?: (outputPage?: string) => void;
  }
}

export type SimBriefFields = {
  orig: string;
  dest: string;
  type: string;
  reg: string;
  airline: string;
  fltnum: string;
  callsign: string;
  pax: string;
  date: string;
  deph: string;
  depm: string;
  units: string;
  navlog: string;
  notams: string;
  maps: string;
  acdata: string;
  cpt: string;
  steh: string;
  stem: string;
  resvrule: string;
  cargo?: string;
  username?: string;
  /** Letters, numbers, underscore — used to retrieve this ticket's OFP later. */
  static_id?: string;
  manualrmk?: string;
};

export type CharterClockSource = "iso_instant" | "halifax_wall" | "unix" | "invalid";

export type CharterClock = {
  instant: Date;
  isoUtc: string;
  date: string;
  deph: string;
  depm: string;
  displayUtc: string;
  source: Exclude<CharterClockSource, "invalid">;
};

const MONTH_INDEX: Record<string, number> = Object.fromEntries(MONTHS.map((m, i) => [m, i]));

function utcClockParts(instant: Date): { date: string; deph: string; depm: string; displayUtc: string } {
  const dd = String(instant.getUTCDate()).padStart(2, "0");
  const mon = MONTHS[instant.getUTCMonth()] ?? "JAN";
  const yy = String(instant.getUTCFullYear()).slice(-2);
  return {
    date: `${dd}${mon}${yy}`,
    deph: String(instant.getUTCHours()).padStart(2, "0"),
    depm: String(instant.getUTCMinutes()).padStart(2, "0"),
    displayUtc: formatUtcDateTime(instant),
  };
}

function fromInstant(instant: Date, source: Exclude<CharterClockSource, "invalid">): CharterClock {
  const parts = utcClockParts(instant);
  return {
    instant,
    isoUtc: instant.toISOString(),
    source,
    ...parts,
  };
}

/**
 * Resolve a requested charter time to a UTC instant.
 * - ISO with `Z` or ±offset is an instant.
 * - Naive `YYYY-MM-DD[ T]HH:mm[:ss]` is America/Halifax wall time.
 * - Desk labels like `15 JAN 2026  12:30 AST` are Halifax (or UTC when the zone is UTC).
 * - Unix seconds / ms are instants.
 */
export function parseCharterInstant(raw: string | Date | number | null | undefined): Date | null {
  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? null : raw;
  }
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const ms = raw < 1e12 ? raw * 1000 : raw;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof raw !== "string") return null;
  const s = raw.trim();
  if (!s) return null;

  if (/^\d{10,13}$/.test(s)) {
    const n = Number(s);
    const ms = s.length <= 10 ? n * 1000 : n;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (/[zZ]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s)) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const naive = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/);
  if (naive) {
    return halifaxWallToDate(
      Number(naive[1]),
      Number(naive[2]),
      Number(naive[3]),
      Number(naive[4]),
      Number(naive[5]),
      Number(naive[6] ?? 0),
    );
  }

  const dateOnly = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    return halifaxWallToDate(Number(dateOnly[1]), Number(dateOnly[2]), Number(dateOnly[3]), 0, 0, 0);
  }

  const desk = s.match(
    /^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})\s+(\d{1,2}):(\d{2})(?:\s*(ADT|AST|UTC))?/i,
  );
  if (desk) {
    const mon = MONTH_INDEX[desk[2].toUpperCase()];
    if (mon != null) {
      const zone = (desk[6] ?? "").toUpperCase();
      if (zone === "UTC") {
        return new Date(Date.UTC(Number(desk[3]), mon, Number(desk[1]), Number(desk[4]), Number(desk[5])));
      }
      return halifaxWallToDate(Number(desk[3]), mon + 1, Number(desk[1]), Number(desk[4]), Number(desk[5]), 0);
    }
  }

  const fallback = new Date(s);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

export function charterClockSource(raw: string | Date | number | null | undefined): CharterClockSource {
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? "invalid" : "iso_instant";
  if (typeof raw === "number") return Number.isFinite(raw) ? "unix" : "invalid";
  if (typeof raw !== "string" || !raw.trim()) return "invalid";
  const s = raw.trim();
  if (/^\d{10,13}$/.test(s)) return "unix";
  if (/[zZ]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s)) return "iso_instant";
  if (/^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2})?/.test(s)) return "halifax_wall";
  if (/^\d{1,2}\s+[A-Za-z]{3}\s+\d{4}\s+\d{1,2}:\d{2}/.test(s)) return "halifax_wall";
  return parseCharterInstant(s) ? "iso_instant" : "invalid";
}

/** Convert a desk-stored charter time to SimBrief's UTC-only date / deph / depm. */
export function simbriefClockFromCharter(raw: string | Date | number | null | undefined): CharterClock | null {
  const instant = parseCharterInstant(raw);
  if (!instant) return null;
  const source = charterClockSource(raw);
  if (source === "invalid") return fromInstant(instant, "iso_instant");
  return fromInstant(instant, source);
}

/** @deprecated prefer simbriefClockFromCharter — still UTC, but assumes `iso` is already an instant. */
export function simbriefDate(iso: string): { date: string; deph: string; depm: string } {
  const clock = simbriefClockFromCharter(iso);
  if (!clock) return { date: "", deph: "", depm: "" };
  return { date: clock.date, deph: clock.deph, depm: clock.depm };
}

export function flightNumber(callsign: string): string {
  const m = callsign.match(/(\d+)/);
  return m?.[1] ?? "80";
}

export function atcCallsign(callsign: string): string {
  return callsign.replace(/\s+/g, "").toUpperCase();
}

/** Public static_id charset is letters, numbers, underscore. */
export function staticIdFromMissionId(id: string): string {
  const cleaned = id
    .trim()
    .replace(/[^A-Za-z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 64);
  return cleaned || "NS_PLAN";
}

export function dispatchRemark(purpose: string): string {
  return purpose.replace(/\s+/g, " ").trim().slice(0, 180);
}

export function hawkerAcdata(spec: AircraftSpec): string {
  const oew = (spec.oewLb / 1000).toFixed(3);
  const mzfw = (spec.mzfwLb / 1000).toFixed(3);
  const mtow = (spec.mtowLb / 1000).toFixed(3);
  const mlw = (spec.mlwLb / 1000).toFixed(3);
  const maxfuel = (spec.maxFuelLb / 1000).toFixed(3);
  return JSON.stringify({
    cat: "M",
    equip: "SDFGHIWYR",
    transponder: "S",
    pbn: "PBN/B2B3B4D2",
    maxpax: String(spec.maxPax),
    oew: Number(oew),
    mzfw: Number(mzfw),
    mtow: Number(mtow),
    mlw: Number(mlw),
    maxfuel: Number(maxfuel),
    per: "C",
    // Body weight only. bagwgt is SimBrief's built-in bags so a custom
    // paxwgt does not drop them or stack a second allowance on top.
    // FlightFX Hawker pax/bag sliders are a separate load UI.
    paxwgt: 200,
    bagwgt: SIMBRIEF_INCLUDED_BAG_LB,
  });
}

function regKey(value: string | undefined): string {
  return (value ?? "").toUpperCase().replace(/\s+/g, "");
}

/**
 * The leg's airframe is the tail on the fleet that was passed in.
 * A missing fleet, or a fleet that does not contain the tail, stays blank.
 */
function specForSimbriefLeg(mission: DraftMission, fleet: AircraftSpec[] | undefined): AircraftSpec {
  const rows = fleet ?? [];
  const reg = regKey(mission.registration);
  const hit =
    rows.find((row) => row.id === mission.aircraftId) ??
    (reg ? rows.find((row) => regKey(row.registration) === reg) : undefined);
  if (hit) return hit;
  return blankAircraft({
    id: mission.aircraftId || "unset",
    registration: mission.registration || "",
    name: mission.aircraftName || "",
  });
}

export function fieldsForLeg(
  mission: DraftMission,
  leg: FlightLeg,
  opts: {
    username?: string;
    registration?: string;
    captain?: string;
    fleet?: AircraftSpec[];
    cargoLb?: number;
    /** Company flightNumberPrefix — derives the SimBrief airline code. Empty ⇒ no airline code. */
    companyPrefix?: string;
  } = {},
): SimBriefFields {
  const spec = specForSimbriefLeg(mission, opts.fleet);
  const clock =
    simbriefClockFromCharter(leg.departureIso) ??
    simbriefClockFromCharter(mission.departureIso) ??
    fromInstant(new Date(0), "iso_instant");
  const eteH = Math.floor(leg.eteMin / 60);
  const eteM = leg.eteMin % 60;
  const reg = (opts.registration || mission.registration || spec.registration).replace(/\s+/g, "");
  const reserve = spec.reserveMin > 0 ? spec.reserveMin : 45;
  const fields: SimBriefFields = {
    orig: leg.originIcao.toUpperCase(),
    dest: leg.destIcao.toUpperCase(),
    type: spec.icaoType,
    reg,
    airline: simbriefAirlineCode(opts.companyPrefix),
    fltnum: flightNumber(mission.callsign),
    callsign: atcCallsign(mission.callsign),
    pax: String(leg.paxCount),
    date: clock.date,
    deph: clock.deph,
    depm: clock.depm,
    units: "LBS",
    navlog: "1",
    notams: "1",
    maps: "detail",
    acdata: hawkerAcdata(spec),
    cpt: (opts.captain ?? "").toUpperCase(),
    steh: String(eteH),
    stem: String(eteM).padStart(2, "0"),
    resvrule: String(reserve),
    static_id: staticIdFromMissionId(mission.id),
  };
  if (opts.username) fields.username = opts.username;
  const remark = dispatchRemark(mission.purpose || mission.scenario || "");
  if (remark) fields.manualrmk = remark;
  const cargoLb = opts.cargoLb ?? mission.cargoLb ?? 0;
  fields.cargo = simbriefCargoParam(simbriefExtraFreightLb(leg.paxCount, cargoLb, mission.flightType));
  return fields;
}

function appendFieldParams(params: URLSearchParams, fields: SimBriefFields) {
  for (const [key, value] of Object.entries(fields)) {
    if (value === undefined || value === "") continue;
    params.set(key, value);
  }
}

export function dispatchUrl(fields: SimBriefFields, opts: { legacy?: boolean } = {}): string {
  const params = new URLSearchParams();
  if (opts.legacy) params.set("newflight", "1");
  appendFieldParams(params, fields);
  const base = opts.legacy ? LEGACY_DISPATCH_URL : DISPATCH_OPTIONS_URL;
  return `${base}?${params.toString()}`;
}

export function clockLabelFromFields(fields: SimBriefFields): string {
  if (!fields.date || !fields.deph) return "";
  return `${fields.date} ${fields.deph}${fields.depm}Z`;
}

function ensureForm(): HTMLFormElement {
  let form = document.getElementById("sbapiform") as HTMLFormElement | null;
  if (!form) {
    form = document.createElement("form");
    form.id = "sbapiform";
    form.style.display = "none";
    document.body.appendChild(form);
  }
  return form;
}

function writeFields(form: HTMLFormElement, fields: SimBriefFields, apiKey?: string) {
  form.replaceChildren();
  const add = (name: string, value: string) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  };
  for (const [key, value] of Object.entries(fields)) {
    if (value === undefined || value === "") continue;
    add(key, value);
  }
  if (apiKey) add("apikey", apiKey);
}

/** Hosts whose generate / sign-in pages may be opened from the desk. */
const GENERATE_HOST_RE = /^(?:[a-z0-9-]+\.)*(?:simbrief|navigraph)\.com$/i;

export function isAllowedGenerateUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" && GENERATE_HOST_RE.test(parsed.hostname);
  } catch {
    return false;
  }
}

export type OpenGenerateMode = "popup" | "tab" | "blocked";

export type GenerateOpenResult =
  | { ok: true; mode: "external" | "popup" | "tab"; url: string }
  | { ok: false; mode: "blocked"; url: string; message: string };

/** Open the prefilled generate page in a named popup, then a tab. */
export function openGenerateWindow(url: string): OpenGenerateMode {
  if (typeof window === "undefined") return "blocked";
  if (!isAllowedGenerateUrl(url)) return "blocked";
  const popup = window.open(url, "NSPlanGenerate", "width=1080,height=720");
  if (popup) return "popup";
  const tab = window.open(url, "_blank");
  return tab ? "tab" : "blocked";
}

/**
 * Open the generate page. Prefer a Tauri/system-browser opener (OAuth cookies
 * and Navigraph sign-in fail inside the desk webview / iframe). If that is
 * missing, try `window.open`. If the popup is blocked, try the external opener
 * so the desk never dead-ends.
 */
export async function resolveGenerateOpen(
  url: string,
  opts: {
    openExternal?: (href: string) => Promise<boolean>;
    preferExternal?: boolean;
  } = {},
): Promise<GenerateOpenResult> {
  if (!isAllowedGenerateUrl(url)) {
    return { ok: false, mode: "blocked", url, message: GENERATE_URL_REFUSED };
  }

  const tryExternal = async (): Promise<boolean> => {
    if (!opts.openExternal) return false;
    try {
      return Boolean(await opts.openExternal(url));
    } catch {
      return false;
    }
  };

  if (opts.preferExternal && (await tryExternal())) {
    return { ok: true, mode: "external", url };
  }

  const win = openGenerateWindow(url);
  if (win !== "blocked") {
    return { ok: true, mode: win, url };
  }

  if (await tryExternal()) {
    return { ok: true, mode: "external", url };
  }

  return { ok: false, mode: "blocked", url, message: GENERATE_POPUP_BLOCKED };
}

export function sendToSimBrief(
  fields: SimBriefFields,
  opts: { apiKey?: string; outputPage?: string } = {},
): "api" | "dispatch" {
  const form = ensureForm();
  writeFields(form, fields, opts.apiKey);
  const output =
    opts.outputPage ??
    `${window.location.origin}${window.location.pathname.replace(/\/$/, "")}/simbrief-ofp`;

  if (typeof window.simbriefsubmit === "function") {
    window.simbriefsubmit(output);
    return "api";
  }

  const url = dispatchUrl(fields);
  if (openGenerateWindow(url) === "blocked") {
    openGenerateWindow(dispatchUrl(fields, { legacy: true }));
  }
  return "dispatch";
}

export const GENERATE_NEEDS_TICKET =
  "Select a filed ticket so Request new plan has fields to send.";

export const GENERATE_NEEDS_FIELDS =
  "Request new plan needs origin, destination, and aircraft type from the selected ticket.";

export const GENERATE_OPENED_EXTERNAL =
  "Opened the generate page in your system browser so SimBrief / Navigraph sign-in can use normal cookies. After you generate, return here and Retrieve latest.";

export const GENERATE_POPUP_BLOCKED =
  "The desk could not open a window. Copy the generate URL from Flight plans or Settings and paste it in your system browser.";

export const GENERATE_URL_REFUSED =
  "That generate URL is not a SimBrief or Navigraph address, so the desk will not open it.";

/** @deprecated Generate no longer waits on a provider API key. */
export const GENERATE_NEEDS_KEY =
  "Request new plan no longer needs an API key. It opens a prefilled generate page from the selected ticket.";

/** @deprecated Official JS client is unused; generate uses the public dispatch URL. */
export const GENERATE_NOT_WIRED =
  "Request new plan uses the public generate page. Retrieve latest plan still works with username or Pilot ID.";

export type GenerateOfpResult =
  | { ok: true; mode: "dispatch"; url: string; legacyUrl: string; utcLabel: string }
  | { ok: false; reason: "no_fields"; message: string };

export function hasSimbriefApiKey(apiKey?: string): boolean {
  return Boolean(apiKey?.trim());
}

/**
 * Build the public prefilled generate URLs. No API key.
 * The desk opens `url` in the system browser (Tauri opener) so Navigraph
 * sign-in works. `legacyUrl` is the alternate host; copy-URL is last resort.
 */
export function attemptGenerateOfp(fields: SimBriefFields): GenerateOfpResult {
  if (!fields.orig?.trim() || !fields.dest?.trim() || !fields.type?.trim()) {
    return { ok: false, reason: "no_fields", message: GENERATE_NEEDS_FIELDS };
  }
  return {
    ok: true,
    mode: "dispatch",
    url: dispatchUrl(fields),
    legacyUrl: dispatchUrl(fields, { legacy: true }),
    utcLabel: clockLabelFromFields(fields),
  };
}

export function halifaxFlightWhen(iso: string): string {
  const p = halifaxParts(new Date(iso));
  return `${p.day} ${p.month} ${p.hour}:${p.minute}`;
}
